import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { redirectTopLevel } from "@/lib/redirect";
import { useAuth } from "@/context/AuthContext";
import { Check, Loader2, Lock, ArrowRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const FEATURES = [
  "Personalized AI workout plan",
  "AI-generated nutrition plan & meal breakdowns",
  "24/7 FORGE-AI coach chat — adjust anything",
  "Macro tracker w/ AI food photo recognition",
  "Strong-style workout log with PR detection",
  "Saved plans & full history",
  "Weekly newsletter drops",
];

const CARDS = [
  {
    id: "monthly",
    label: "MONTHLY",
    headline: "9.99",
    sub: "PER MONTH",
    cadence: "/ month",
    bullets: ["Billed monthly", "Cancel anytime", "Same full access"],
    cta: "Join Monthly",
    accent: false,
    save: null,
  },
  {
    id: "quarterly",
    label: "EVERY 3 MONTHS",
    headline: "25.99",
    sub: "BILLED EVERY 3 MONTHS",
    cadence: "/ 3 months",
    bullets: ["Save 13% vs monthly", "$8.66 effective / month", "Auto-renews quarterly"],
    cta: "Go Quarterly",
    accent: true,
    save: "SAVE 13%",
  },
  {
    id: "yearly",
    label: "YEARLY",
    headline: "79.99",
    sub: "BILLED ANNUALLY",
    cadence: "/ year",
    bullets: ["Save 33% vs monthly", "$6.66 effective / month", "Best long-term value"],
    cta: "Go Yearly",
    accent: false,
    save: "SAVE 33%",
  },
];

export default function Pricing() {
  const { user, loading: authLoading } = useAuth();
  const [billing, setBilling] = useState(null);
  const [redirecting, setRedirecting] = useState(null); // plan id being processed

  useEffect(() => {
    if (user) api.get("/billing/status").then((r) => setBilling(r.data)).catch(() => {});
  }, [user]);

  if (authLoading) return null;

  const joinNow = async (planId) => {
    setRedirecting(planId);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan: planId,
        origin_url: window.location.origin,
      });
      if (!data?.url) throw new Error("Stripe did not return a checkout URL");
      redirectTopLevel(data.url);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Could not start checkout — please try again.";
      toast.error(msg, { duration: 6000 });
      setRedirecting(null);
    }
  };

  const needsPayment = billing?.needs_payment_update;
  const alreadyActive = billing?.active && !needsPayment;

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-16" data-testid="pricing-page">
      <div className="text-center mb-12">
        <div className="font-mono text-[10px] tracking-[0.4em] text-[#FF3333]">FORGE-AI · MEMBERS ONLY</div>
        <h1 className="font-heading uppercase text-6xl md:text-7xl leading-[0.9] mt-3">
          One Membership. <br /><span className="text-stroke-red grunge-text">Three Cadences.</span>
        </h1>
        <p className="font-sub text-white/60 mt-4 max-w-xl mx-auto">
          Same full access. Pick what suits your wallet. Cancel anytime.
        </p>
      </div>

      {alreadyActive && (
        <div className="brutal-border-blue bg-[#00E5FF]/5 p-4 mb-8 text-center" data-testid="pricing-already-active">
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#00E5FF] mb-1">SUBSCRIPTION ACTIVE</div>
          <div className="font-sub text-white/80">
            You&apos;re a Forged member on the{" "}
            <strong className="text-white">
              {{ monthly: "Monthly", quarterly: "Every 3 Months", yearly: "Yearly" }[billing.plan] || billing.plan}
            </strong>{" "}
            plan. Status: {billing.status}.
          </div>
          <Link to="/dashboard" className="btn-forged-ghost mt-3 inline-flex">Back to Dashboard</Link>
        </div>
      )}

      {needsPayment && (
        <div className="brutal-border-red bg-[#FF3333]/10 p-5 mb-8 flex items-start gap-3" data-testid="pricing-payment-required">
          <AlertTriangle className="w-5 h-5 text-[#FF3333] flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] text-[#FF3333]">PAYMENT REQUIRED</div>
            <div className="font-sub text-white/85 mt-1">
              Your last payment didn&apos;t go through ({billing.status}). Update your card to restore access.
            </div>
            <Link to="/dashboard" className="btn-forged-red mt-3 inline-flex">Update Payment</Link>
          </div>
        </div>
      )}

      {/* Three plan cards */}
      <div className="grid md:grid-cols-3 gap-6">
        {CARDS.map((card) => {
          const isAccent = card.accent;
          const cardClass = isAccent
            ? "brutal-border-red bg-gradient-to-br from-[#FF3333]/15 via-[#0a0a0a] to-[#0a0a0a] md:scale-[1.04]"
            : "brutal-border bg-[#0a0a0a]";
          const btnClass = isAccent ? "btn-forged-red" : "btn-forged-ghost";

          return (
            <div
              key={card.id}
              className={`relative p-8 md:p-9 flex flex-col ${cardClass}`}
              data-testid={`pricing-${card.id}`}
            >
              {isAccent && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#FF3333] text-black font-mono text-[10px] tracking-[0.3em]">
                  BEST VALUE
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="font-mono text-[10px] tracking-[0.4em] text-[#FF3333]">{card.label}</div>
                {card.save && (
                  <div className="font-mono text-[9px] tracking-[0.3em] text-[#00FF66]" data-testid={`pricing-${card.id}-save`}>
                    {card.save}
                  </div>
                )}
              </div>

              <div className="mt-5 flex items-baseline gap-1">
                <span className="font-heading text-[#FF3333] text-2xl">$</span>
                <span className="font-heading text-white text-[4.5rem] leading-none">{card.headline}</span>
                <span className="font-mono text-[10px] text-white/40 ml-1 self-end mb-2">{card.cadence}</span>
              </div>
              <div className="font-mono text-[9px] tracking-[0.4em] text-white/40 mt-1">
                {card.sub}
              </div>

              <ul className="mt-6 space-y-2 flex-1">
                {card.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 font-sub text-sm text-white/75">
                    <Check className="w-3.5 h-3.5 text-[#FF3333] flex-shrink-0 mt-1" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => joinNow(card.id)}
                disabled={redirecting !== null || alreadyActive}
                data-testid={`pricing-join-${card.id}`}
                className={`${btnClass} w-full justify-center mt-8 py-4 disabled:opacity-40`}
              >
                {redirecting === card.id ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting</>
                ) : alreadyActive ? (
                  <><Check className="w-4 h-4" /> Member</>
                ) : (
                  <>{card.cta} <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex flex-col items-center gap-2">
        <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-white/40">
          <Lock className="w-3 h-3" /> SECURE CHECKOUT BY STRIPE · CANCEL ANYTIME
        </div>
        {!user && (
          <Link
            to="/auth"
            data-testid="pricing-already-member"
            className="font-mono text-[11px] tracking-widest text-white/60 hover:text-[#FF3333] transition-colors mt-2"
          >
            ALREADY A MEMBER? <span className="text-[#FF3333] underline">LOG IN</span>
          </Link>
        )}
      </div>

      {/* What you get */}
      <div className="brutal-border bg-[#0a0a0a] p-8 mt-12">
        <div className="font-mono text-[10px] tracking-[0.4em] text-[#FF3333] mb-3">EVERY PLAN UNLOCKS</div>
        <ul className="grid md:grid-cols-2 gap-3 mt-4">
          {FEATURES.map((f) => (
            <li key={f} className="flex items-center gap-3 font-sub text-white/80">
              <Check className="w-4 h-4 text-[#FF3333] flex-shrink-0" />
              {f}
            </li>
          ))}
        </ul>
      </div>

      <div className="text-center mt-12 font-mono text-[10px] tracking-widest text-white/30">
        ALL TRANSACTIONS PROCESSED SECURELY BY STRIPE · MANAGE YOUR BILLING ANY TIME FROM THE DASHBOARD
      </div>
    </div>
  );
}
